/**
 * 이상한 방 - 매치 세션의 입력 번역 (탭 한 발 → 기하 → `shoot` · 버튼 → 이벤트)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.3절(탄환 경제) · 4.4절(원래 방)
 *            `이상한방-상태모델-v0.1.md` 6.1절(두 히트 경로) · 11.3절(서버가 쥐는 것 - 판정)
 *            로드맵 M2 2.5절(클라이언트는 판정하지 않는다) · M4 준비 몫(`matchSession.ts` 400줄 분할 - 동작 변화 0)
 *
 * `matchSession.ts`에서 갈라져 나왔다. 싱글의 `session.shootAt`에 해당하는 자리이고 원칙은 더 엄하다 -
 * **판정을 하지 않는다.** 탭은 `pickAt → toHitTarget`(기하)까지만 하고 `shoot`을 보낸다. 화면에 찍는 것은 회색 고리
 * 하나(`pending`)고, 색·글자·자막탑은 `shotResolved`가 돌아온 뒤에 `matchSessionEffects.ts`가 찍는다. 잔탄도 미리 깎지
 * 않는다 - 뷰가 오면 HUD가 그 값을 그린다. "지금 쏠 수 있는가"조차 뷰의 불리언(`self.canShoot`)을 읽는다.
 *
 * 루프백은 배달을 마이크로태스크로 미루므로(`loopbackTransport.ts`) "보냈더니 즉시 왔다"에 기대는 코드가 생길 수 없고,
 * 그것이 서버 판의 100ms 지연에서도 같은 코드가 돌게 하는 장치다.
 */

import type { Catalog } from '../core/room'
import type { RoomBounds, RoomState } from '../core/types'
import { t } from '../i18n'
import type { IsoCamera } from '../input/isoCamera'
import { pickAt } from '../input/tapPicker'
import type { SceneCtx } from '../render/scene'
import type { GameHudHandle } from '../ui/gameHud'
import { toHitTarget, type HitTarget } from './hunt'
import type { ClientView } from './match/matchView'
import type { PlayerEvent } from './match/matchTypes'
import type { PendingShot } from './matchSessionEffects'
import type { MatchScene } from './matchSessionScene'
import { isMeInOrigin } from './matchSessionView'
import { toShotResult } from './sessionEffects'
import type { WarpVeil } from './sessionWarp'

/* --- 타입 ------------------------------------------------------------------- */

/** 입력 번역이 세션에서 빌려 쓰는 것 전부. 세션 클로저가 아니라 **이 인자만** 본다 - 옮긴 것이 배선임이 드러나게. */
export type MatchInputParts = Readonly<{
  ctx: SceneCtx
  iso: IsoCamera
  bounds: RoomBounds
  catalog: Catalog
  hud: GameHudHandle
  warpVeil: WarpVeil
  scene: MatchScene
  /** 판정을 기다리는 탭들(도착 순). 여기서 밀어 넣고 번역기(`matchSessionEffects.ts`)가 꺼낸다. */
  pendingShots: PendingShot[]
  /** 지금 뷰. `welcome` 전에는 `null` - 그때는 어떤 입력도 보내지 않는다. */
  view: () => ClientView | null
  /** 세션이 깨어 있는가. 잠든 무대(배치 모드)의 탭은 사격이 아니다. */
  isActive: () => boolean
  /** 이벤트 하나를 호스트로. 돌려주는 `cseq`로 거절을 짝짓는다(세션의 `send`). */
  send: (event: PlayerEvent) => number
}>

export type MatchInput = Readonly<{
  shootAt: (clientX: number, clientY: number) => void
  toggleOrigin: () => void
  useMagazine: () => void
  /** 걸기(M7). 탄창과 같다 - 누르면 언제나 보내고 판정은 호스트다(`matchShoot.onRaise`). */
  raise: () => void
  skipPhase: () => void
}>

/* --- 순수 헬퍼 --------------------------------------------------------------- */

/** `HitTarget` → 기다리는 표식의 종류(기하). */
function pendingKindOf(target: HitTarget | null): PendingShot['kind'] {
  if (target === null) return null
  return target.kind === 'proxy' ? 'removed' : 'object'
}

/* --- 본체 ------------------------------------------------------------------- */

export function createMatchInput(parts: MatchInputParts): MatchInput {
  const { ctx, iso, bounds, catalog, hud, warpVeil, scene, pendingShots } = parts

  /**
   * 탭 한 발. 기하(`pickAt → toHitTarget`)까지만 하고 호스트에 보낸다. 판정은 `shotResolved`를 기다린다(머리말).
   * 쏠 수 없는 상태(`canShoot` 거짓 - 원래 방 안 · 잔탄 0 · 관전)는 뷰가 이미 말하고 있으므로 보내지 않고 이유만 띄운다 -
   * 그것은 판정이 아니라 HUD가 버튼을 비활성으로 그리는 것과 같은 뷰 읽기다.
   */
  function shootAt(clientX: number, clientY: number): void {
    const view = parts.view()
    if (!parts.isActive() || view === null || view.phase !== 'hunt') return

    // 방이 아직 안 바뀐 프레임의 한 발은 보내지 않는다(`session.shootAt`의 같은 창 - 원래 방 왕복이 정답 추출기가 된다).
    if (warpVeil.isBeforeSwap()) {
      hud.flashShot(clientX, clientY, 'dry', t('hud.warpHint'))
      return
    }
    if (!view.self.canShoot) {
      // 왜 못 쏘는지를 그 자리에 말한다(GLOSSARY 3절). 관전이 먼저다 - 관전자는 원래 방에 있을 수 없다(`matchView.originStatusOf`).
      // M11에서 역할이 사라져 "다녀간 사람이라 못 쏜다"는 갈래가 없어졌다(전원이 찾는 사람이다). 남는 경우(잔탄 0인데
      // 관전 전)는 `dry`의 기본 라벨("탄환 없음")이 맞는 말이다.
      const reason = view.self.spectating
        ? t('spectate.shotBlocked')
        : isMeInOrigin(view)
          ? t('hud.originHint')
          : undefined
      hud.flashShot(clientX, clientY, 'dry', reason)
      return
    }

    // 판정 대상은 **화면에 그려져 있는 방**이어야 한다(원래 방에 있는 동안 변경본으로 찍으면 원본에만 있는 것이 안 잡힌다).
    const room: RoomState = scene.visibleRoom(isMeInOrigin(view))
    const pick = pickAt(ctx, clientX, clientY, room, catalog, bounds, { mode: 'search', zoom: iso.getZoom() })
    const target = toHitTarget(toShotResult(pick))

    // 회색 고리 하나, 글자 없음(빈 라벨). 색·글자는 판정이 오면 `matchSessionEffects`가 찍는다. 잔탄은 건드리지 않는다.
    // `shot.pending.label`은 `ShotMarkKind` 조립 키의 완결성용이고 화면에는 나가지 않는다(`gameHud.css`의 같은 결정).
    hud.flashShot(clientX, clientY, 'pending', '')
    const id = parts.send({ type: 'shoot', hit: target })
    pendingShots.push({ x: clientX, y: clientY, cseq: id, kind: pendingKindOf(target) })
  }

  /** 원래 방 토글. 안에 있으면 나가기, 아니면 들어가기 요청(줄서기·예약 수락도 같은 이벤트다 - `matchOrigin.ts`). */
  function toggleOrigin(): void {
    const view = parts.view()
    if (view === null) return
    parts.send({ type: isMeInOrigin(view) ? 'leaveOrigin' : 'requestOrigin' })
  }

  function useMagazine(): void {
    parts.send({ type: 'useMagazine' })
  }

  function raise(): void {
    parts.send({ type: 'raise' })
  }

  function skipPhase(): void {
    parts.send({ type: 'skipPhase' })
  }

  return { shootAt, toggleOrigin, useMagazine, raise, skipPhase }
}
